import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, MessageSquare } from "lucide-react";
import AnimatedDashboard from "@/components/AnimatedDashboard";
import { siteConfig } from "@/config/site";

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Hero() {
  const [lineIdx, setLineIdx] = useState(0);
  const lines = siteConfig.heroLines;

  useEffect(() => {
    const t = setInterval(() => {
      setLineIdx((i) => (i + 1) % lines.length);
    }, 4800);
    return () => clearInterval(t);
  }, [lines.length]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden"
    >
      {/* Ambient orbs — logo brand blur */}
      <div className="vs-orb" style={{ width: 520, height: 520, top: -120, left: -140, background: "var(--vs-ambient-purple)" }} />
      <div className="vs-orb" style={{ width: 620, height: 620, bottom: -200, right: -180, background: "var(--vs-ambient-teal)" }} />
      {/* Grid lines */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.12]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        }}
      />

      <div className="vs-container relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left — Copy */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="flex items-center gap-3"
            >
              <span className="h-px w-10 bg-[#d4af37]" />
              <span className="text-overline">Virtual CFO · Financial Intelligence</span>
            </motion.div>

            <motion.h1
              data-testid="hero-headline"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.22 }}
              className="mt-8 font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-white"
            >
              See Your Business
              <br />
              <span className="italic text-white/95">Through Every</span>
              <br />
              <span className="vs-gradient-text">Financial Lens.</span>
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.4 }}
              className="mt-8 max-w-xl min-h-[112px] md:min-h-[128px] relative"
              data-testid="hero-subline-slider"
            >
              <AnimatePresence mode="wait">
                <motion.p
                  key={lineIdx}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className="text-base md:text-lg text-white/70 font-body font-light leading-relaxed"
                >
                  {lines[lineIdx]}
                </motion.p>
              </AnimatePresence>

              <div className="mt-5 flex items-center gap-1.5">
                {lines.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setLineIdx(i)}
                    aria-label={`Show subheadline ${i + 1}`}
                    data-testid={`hero-slide-dot-${i}`}
                    className={`h-[3px] transition-all duration-500 ${
                      i === lineIdx ? "w-8 bg-[#d4af37]" : "w-4 bg-white/15 hover:bg-white/30"
                    }`}
                  />
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.55 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-cta-book"
                onClick={() => scrollTo("#contact")}
                className="vs-btn-primary group"
              >
                Book Free Consultation
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
              <button
                data-testid="hero-cta-call"
                onClick={() => scrollTo("#contact")}
                className="vs-btn-secondary"
              >
                <MessageSquare size={15} />
                Talk to a CFO
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.0, delay: 0.85 }}
              className="mt-14 grid grid-cols-3 max-w-lg divide-x divide-white/10 border-t border-b border-white/10 py-2"
              data-testid="hero-metrics"
            >
              {[
                { v: "9", k: "Advisory pillars" },
                { v: "3", k: "Regions served" },
                { v: "24/7", k: "Owner-first access" },
              ].map((m) => (
                <div key={m.k} className="py-5 px-5 text-left first:pl-2 last:pr-2">
                  <div className="vs-metric text-3xl md:text-4xl leading-none">{m.v}</div>
                  <div className="text-[10px] tracking-widest text-white/50 uppercase font-body mt-2">
                    {m.k}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right — Dashboard */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, delay: 0.5 }}
            className="lg:col-span-5 relative"
          >
            <div
              className="absolute -inset-6 rounded-lg opacity-70 blur-3xl -z-10"
              style={{
                background:
                  "radial-gradient(circle at 30% 20%, rgba(115,81,183,0.25), transparent 60%), radial-gradient(circle at 80% 80%, rgba(27,147,164,0.2), transparent 60%)",
              }}
            />
            <AnimatedDashboard />

            {/* Floating accent card */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 1.1 }}
              className="hidden md:flex absolute -left-10 -bottom-6 vs-glass-strong px-4 py-3 rounded-sm items-center gap-3 shadow-xl"
            >
              <div className="h-8 w-8 rounded-sm border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37] font-serif-display text-lg">
                ✦
              </div>
              <div>
                <div className="text-[10px] tracking-widest text-white/50 uppercase">
                  Insight surfaced
                </div>
                <div className="text-sm text-white font-body">
                  4.2% margin leak in F&amp;B outlets
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Trust marquee */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.3 }}
          className="mt-24 md:mt-28"
        >
          <div className="text-[10px] tracking-[0.4em] text-white/40 uppercase text-center font-body">
            Advising business owners across
          </div>
          <div className="mt-6 overflow-hidden relative">
            <div className="vs-marquee-track flex gap-16 whitespace-nowrap will-change-transform">
              {[...Array(2)].flatMap((_, i) =>
                [
                  "Dubai",
                  "Abu Dhabi",
                  "Doha",
                  "Riyadh",
                  "Bengaluru",
                ].map((c, j) => (
                  <span
                    key={`${i}-${j}`}
                    className="font-serif-display text-2xl md:text-3xl text-white/25"
                  >
                    {c} <span className="text-[#d4af37]/60 mx-2">·</span>
                  </span>
                ))
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
